import { useState } from 'react';

import { ConfirmDelete } from '@backrs_ui/common-ui/src/atoms/ConfirmDelete';
import { FamilyMemberTableRow } from '@backrs_ui/common-ui/src/molecules/FamilyMemberTableRow/FamilyMemberTableRow';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  useRemoveFamilyMemberMutation,
  useRevokeFamilyInviteMutation,
  useLeaveFamilyMutation,
} from '@backrs_ui/grow-ui/src/redux/family/family.api';
import {
  FamilyAccountMemberRecord,
  FamilyAccountInvite,
  FamilyAccountUser,
} from '@backrs_ui/grow-ui/src/redux/family/family.types';

import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  SxProps,
  Theme,
  styled,
} from '@mui/material';

const STRINGS = appStrings.modules.accountSettings.family.sections.members;

const TableContainerStyled = styled('div')(({ theme }) => ({
  border: `1px solid ${theme.backrs.colors['Colors/Border/border-secondary']}`,
  borderRadius: theme.backrs.radius['radius-md'],
}));

type FamilyMembersTableProps = Readonly<{
  owner?: FamilyAccountUser;
  members: FamilyAccountMemberRecord[];
  invites: FamilyAccountInvite[];
  isOwner: boolean;
  returnToDefaultTab: () => void;
}>;

export const FamilyMembersTable = ({
  owner,
  members,
  invites,
  isOwner,
  returnToDefaultTab,
}: FamilyMembersTableProps) => {
  const [removeFamilyMember, { isLoading: isRemovingFamilyMember }] =
    useRemoveFamilyMemberMutation();

  const [revokeFamilyInvite, { isLoading: isRevokingFamilyInvite }] =
    useRevokeFamilyInviteMutation();

  const [leaveFamily, { isLoading: isLeavingFamily }] = useLeaveFamilyMutation();

  // Confirmation modal state - single state for all action types
  type ConfirmAction =
    | { type: 'leave' }
    | { type: 'remove'; memberId: number }
    | { type: 'revoke'; inviteId: number }
    | null;

  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);

  const handleConfirm = async () => {
    if (!confirmAction) return;

    try {
      switch (confirmAction.type) {
        case 'leave':
          if (isLeavingFamily) return;

          await leaveFamily(undefined).unwrap();
          setConfirmAction(null);
          returnToDefaultTab();
          break;
        case 'remove':
          if (isRemovingFamilyMember) return;

          await removeFamilyMember(confirmAction.memberId).unwrap();
          setConfirmAction(null);
          break;
        case 'revoke':
          if (isRevokingFamilyInvite) return;

          await revokeFamilyInvite(confirmAction.inviteId).unwrap();
          setConfirmAction(null);
          break;
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getModalContent = () => {
    switch (confirmAction?.type) {
      case 'leave':
        return STRINGS.confirmLeave;
      case 'remove':
        return STRINGS.confirmRemove;
      case 'revoke':
        return STRINGS.confirmRevoke;
      default:
        return { header: '', body: '', confirmButton: '' };
    }
  };

  const modalContent = getModalContent();

  const displayName = (user: { first_name: string; last_name: string }) => {
    if (!user.first_name && !user.last_name) return 'Member';
    if (!user.first_name) return user.last_name;
    if (!user.last_name) return user.first_name;

    return `${user.first_name} ${user.last_name}`;
  };

  return (
    <>
      <TableContainer component={TableContainerStyled}>
        <Table size="small" aria-label="family members table">
          <TableHead>
            <TableRow sx={tableRowSx}>
              <TableCell sx={tableHeaderLabelSx}>{STRINGS.nameLabel}</TableCell>
              <TableCell colSpan={2} sx={tableHeaderLabelSx}>
                {STRINGS.roleLabel}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody sx={tableBodySx}>
            {/* Always show owner row when owner is available */}
            {owner && (
              <FamilyMemberTableRow
                key={`family-member-table-row-owner`}
                fullName={displayName(owner)}
                email={owner.email}
                avatarUrl={owner.profile_image}
                role="Subscriber"
              />
            )}
            {/* Show members based on role */}
            {isOwner ? (
              <>
                {members.map((member) => (
                  <FamilyMemberTableRow
                    key={`family-member-table-row-${member.id}`}
                    fullName={displayName(member.user)}
                    email={member.user?.email}
                    avatarUrl={member.user?.profile_image}
                    role={member.family_role || ''}
                    button={
                      <Button
                        id={`family-delete-member-${member.id}`}
                        type="button"
                        onClick={() =>
                          setConfirmAction({ type: 'remove', memberId: member.user?.id })
                        }
                        hierarchy="Tertiary color"
                        sx={buttonSx}
                        size="md"
                      >
                        {STRINGS.deleteLabel}
                      </Button>
                    }
                  />
                ))}
                {invites.map((invite) => (
                  <FamilyMemberTableRow
                    key={`family-member-table-row-invite-${invite.id}`}
                    fullName={invite?.first_name || ''}
                    email={invite.email}
                    role={invite.family_role || ''}
                    button={
                      <Button
                        id={`family-revoke-invite-${invite.id}`}
                        type="button"
                        onClick={() => setConfirmAction({ type: 'revoke', inviteId: invite.id })}
                        hierarchy="Tertiary color"
                        size="md"
                        sx={buttonSx}
                      >
                        {STRINGS.revokeLabel}
                      </Button>
                    }
                    isInvite
                  />
                ))}
              </>
            ) : (
              members.map((member) => (
                <FamilyMemberTableRow
                  key={`family-member-table-row-${member.id}`}
                  fullName={displayName(member.user)}
                  email={member.user?.email}
                  avatarUrl={member.user?.profile_image}
                  role={member.family_role || ''}
                  button={
                    <Button
                      id={`family-leave-${member.id}`}
                      type="button"
                      onClick={() => setConfirmAction({ type: 'leave' })}
                      hierarchy="Tertiary color"
                      sx={buttonSx}
                      size="md"
                    >
                      {STRINGS.leaveLabel}
                    </Button>
                  }
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Confirmation Modal */}
      <ConfirmDelete
        openDialog={confirmAction !== null}
        setOpenDialog={(open) => {
          if (!open) setConfirmAction(null);
        }}
        headerText={modalContent.header}
        bodyText={modalContent.body}
        handleDelete={handleConfirm}
        confirmButtonText={modalContent.confirmButton}
      />
    </>
  );
};

const tableHeaderLabelSx: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-xs-semibold'],
  color: backrs.colors['Colors/Text/text-quaternary'],
  backgroundColor: backrs.colors['Colors/Background/bg-secondary'],
});

const tableRowSx: SxProps<Theme> = ({ backrs }) => ({
  '& > th': {
    paddingBlock: backrs.spacing['spacing-lg'],
  },
  '& > th:first-of-type': {
    paddingLeft: backrs.spacing['spacing-3xl'],
  },
  '& > th:last-of-type': {
    paddingRight: backrs.spacing['spacing-3xl'],
  },
});

const tableBodySx: SxProps<Theme> = {
  '& tr:last-of-type td': {
    border: 0,
  },
};

const buttonSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Gray/600'],
  '&:hover': {
    backgroundColor: backrs.colors['Colors/Base/Transparent'],
    color: backrs.colors['Gray/700'],
  },
  '&.Mui-disabled': {
    color: backrs.colors['Colors/Foreground/fg-disabled'],
    backgroundColor: backrs.colors['Colors/Base/Transparent'],
    border: 'none',
  },
});
